import { isSupabaseEnabled, supabase } from './supabaseClient';
import {
  PAGE_CONTENT_TABLES,
  PAGE_TABLE_NAMES,
  resolvePageSlug,
  toFullContentKey,
  toPageStorageKey,
} from '../data/pageContentTables';

const STORAGE_KEY = 'psag-site-content';
const UPSERT_BATCH_SIZE = 25;
const LEGACY_TABLE = 'site_content';

function loadLocalContent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveLocalContent(content) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(content));
}

function clearLocalContent() {
  localStorage.removeItem(STORAGE_KEY);
}

function valueEquals(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function getChangedKeys(content, previousContent) {
  return Object.keys(content).filter((key) => !valueEquals(content[key], previousContent[key]));
}

function getRemovedKeys(content, previousContent) {
  return Object.keys(previousContent).filter((key) => !Object.prototype.hasOwnProperty.call(content, key));
}

function groupKeysByPage(keys, content) {
  const groups = {};

  keys.forEach((fullKey) => {
    const pageSlug = resolvePageSlug(fullKey);
    const storageKey = toPageStorageKey(fullKey);
    if (!pageSlug || !storageKey) return;

    if (!groups[pageSlug]) {
      groups[pageSlug] = [];
    }

    groups[pageSlug].push({
      storageKey,
      value: content[fullKey],
    });
  });

  return groups;
}

async function upsertTableRows(tableName, rows) {
  for (let index = 0; index < rows.length; index += UPSERT_BATCH_SIZE) {
    const batch = rows.slice(index, index + UPSERT_BATCH_SIZE);
    const { error } = await supabase
      .from(tableName)
      .upsert(batch, { onConflict: 'content_key' });

    if (error) throw error;
  }
}

async function deleteTableRows(tableName, storageKeys) {
  for (let index = 0; index < storageKeys.length; index += UPSERT_BATCH_SIZE) {
    const batch = storageKeys.slice(index, index + UPSERT_BATCH_SIZE);
    const { error } = await supabase.from(tableName).delete().in('content_key', batch);

    if (error) throw error;
  }
}

async function fetchLegacySiteContent() {
  const { data, error } = await supabase.from(LEGACY_TABLE).select('content_key, value');
  if (error) {
    if (error.code === '42P01') return {};
    throw error;
  }

  const content = {};
  (data || []).forEach((row) => {
    content[row.content_key] = row.value;
  });
  return content;
}

async function fetchPageTablesContent() {
  const content = {};

  await Promise.all(
    Object.entries(PAGE_CONTENT_TABLES).map(async ([pageSlug, tableName]) => {
      const { data, error } = await supabase.from(tableName).select('content_key, value');
      if (error) throw error;

      (data || []).forEach((row) => {
        content[toFullContentKey(pageSlug, row.content_key)] = row.value;
      });
    })
  );

  return content;
}

export async function fetchSiteContent() {
  if (!isSupabaseEnabled()) {
    return loadLocalContent();
  }

  try {
    const [pageContent, legacyContent] = await Promise.all([
      fetchPageTablesContent(),
      fetchLegacySiteContent(),
    ]);

    const content = { ...legacyContent, ...pageContent };
    saveLocalContent(content);
    return content;
  } catch (error) {
    console.error('Failed to fetch site content from Supabase:', error);
    return loadLocalContent();
  }
}

export async function saveSiteContent(content, previousContent = {}) {
  const changedKeys = getChangedKeys(content, previousContent);
  const removedKeys = getRemovedKeys(content, previousContent);

  if (changedKeys.length === 0 && removedKeys.length === 0) {
    return { ok: true };
  }

  if (!isSupabaseEnabled()) {
    saveLocalContent(content);
    return { ok: true };
  }

  try {
    const changedByPage = groupKeysByPage(changedKeys, content);
    const removedByPage = groupKeysByPage(removedKeys, previousContent);

    await Promise.all(
      Object.entries(PAGE_CONTENT_TABLES).map(async ([pageSlug, tableName]) => {
        const changedRows = (changedByPage[pageSlug] || []).map((row) => ({
          content_key: row.storageKey,
          value: row.value,
          updated_at: new Date().toISOString(),
        }));

        if (changedRows.length > 0) {
          await upsertTableRows(tableName, changedRows);
        }

        const removedStorageKeys = (removedByPage[pageSlug] || []).map((row) => row.storageKey);
        if (removedStorageKeys.length > 0) {
          await deleteTableRows(tableName, removedStorageKeys);
        }
      })
    );

    saveLocalContent(content);
    return { ok: true };
  } catch (error) {
    console.error('Failed to save site content to Supabase:', error);
    return { ok: false, error };
  }
}

export async function clearSiteContent() {
  clearLocalContent();

  if (!isSupabaseEnabled()) {
    return { ok: true };
  }

  try {
    await Promise.all(
      [...PAGE_TABLE_NAMES, LEGACY_TABLE].map(async (tableName) => {
        const { error } = await supabase.from(tableName).delete().neq('content_key', '');
        if (error && error.code !== '42P01') throw error;
      })
    );

    return { ok: true };
  } catch (error) {
    console.error('Failed to clear site content in Supabase:', error);
    return { ok: false, error };
  }
}

export async function migrateLocalContentToSupabase() {
  const local = loadLocalContent();
  if (!isSupabaseEnabled() || Object.keys(local).length === 0) {
    return;
  }

  const remote = await fetchSiteContent();
  if (Object.keys(remote).length > 0) {
    return;
  }

  await saveSiteContent(local, {});
}

export { PAGE_CONTENT_TABLES, PAGE_TABLE_NAMES };
